import {HttpClient} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';

import {EMPTY, forkJoin, from, Observable, of} from 'rxjs';
import {catchError, finalize, map, mergeMap, pluck, switchMap, takeUntil, tap, toArray} from 'rxjs/operators';

import isEmpty from 'lodash-es/isEmpty';

import * as moment from 'moment';

import {PayValuesService} from './pay-values.service';

import {orderBy, sortedUniqBy} from 'lodash';
import {REQUEST_OPTIONS} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {ApiRequestOptions, ApiService} from '../../shared/api';
import {RegulationFlattensDTO} from '../../shared/api/RepositoryApi';
import {ModalCommand, ModalState} from '../../shared/modal';
import {PayrollType} from '../analyze/pay-exec.model';
import {PayExecService} from '../analyze/pay-exec.service';

export interface HawkAssertion {
  name: string; // payValue name
  value: number; // payValue allValue (editable)
}

export interface HawkExecution {
  executionDate: string; // pay period contract computeDate
  beginDate: string;
  endDate: string;
  assertions: HawkAssertion[];
}

@Component({
  selector: 'gp-hawk-test-creation',
  templateUrl: './hawk-test-creation-modal.component.html',
  styleUrls: ['./hawk-test-creation-modal.component.scss'],
  providers: [PayExecService, PayValuesService]
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class HawkTestCreationModalComponent extends Destroyable implements OnInit {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private payExecService: PayExecService,
    private payValuesService: PayValuesService,
    private apiService: ApiService,
    private httpClient: HttpClient
  ) {
    super();

    this.hawkTestUrl = localStorage.getItem('gp_hawk_test_url');
  }

  public contractId: number = null;
  public cliRegId: number = null;
  public initialRules: string[];
  public rules: string[];

  public hawkTestCreationForm: UntypedFormGroup;
  public hawkTestUrl: string;
  public filtersGroup: UntypedFormGroup;
  public loading = false;
  public hawkExecutions: HawkExecution[];
  public payExecsLoading = false;

  public submitCallback: () => Observable<ModalCommand>;

  ngOnInit() {
    this.initForm();

    this.submitCallback = this.submit.bind(this);

    this.hawkTestCreationForm
      .get('rules')
      .valueChanges
      .pipe(
        tap(() => {
          this.loading = true;
          this.payExecsLoading = true;
        }),
        switchMap(() =>
          this.updateHawkExecution(this.contractId)
            .pipe(
              toArray(),
              map((executions) => {
                this.hawkExecutions = executions;
                this.populateForm(this.hawkExecutions);
                this.payExecsLoading = false;
              })
            )
        )
      )
      .subscribe();

    this.hawkTestCreationForm.patchValue({
      contractId: this.contractId,
      cliRegId: this.cliRegId,
      rules: []
    });

    this.populateInitialRules();
  }

  initForm() {
    this.hawkTestCreationForm = this.formBuilder.group({
      contractId: ['', [Validators.required]],
      cliRegId: ['', [Validators.required]],
      suite: [''],
      feature: ['', [Validators.required, Validators.minLength(3)]],
      title: ['', [Validators.required, Validators.minLength(8)]],
      description: ['', [Validators.required, Validators.minLength(8)]],
      labels: [''],
      rules: [''],
      executions: this.formBuilder.array([], [Validators.minLength(1)])
    });
  }

  populateForm(executions: HawkExecution[]) {
    for (const execution of executions) {
      const beginDate = execution.beginDate;

      const formGroupExecutions = this.executions.controls as UntypedFormGroup[];
      const existingExecution = formGroupExecutions.find((value) => {
        return value.get('beginDate')?.value === beginDate;
      });

      const assertionFormGroup: UntypedFormGroup = this.formBuilder.group({});

      for (const assertion of execution.assertions) {
        assertionFormGroup.setControl(assertion.name, new UntypedFormControl(assertion.value, [Validators.required]));
      }

      if (existingExecution) {
        existingExecution.setControl('assertions', assertionFormGroup);
      } else {
        const executionFormGroup = this.formBuilder.group({
          executionDate: execution.executionDate,
          beginDate: this.formBuilder.control(execution.beginDate),
          endDate: this.formBuilder.control(execution.endDate),
          assertions: assertionFormGroup
        });

        this.executions.push(executionFormGroup);
      }
    }
  }

  public updateHawkExecution(contractId: number): Observable<HawkExecution> {
    const page = 0;

    return this.payExecService
      .getPayExecs(contractId, page)
      .pipe(
        finalize(() => (this.payExecsLoading = false)),
        pluck('data'),
        switchMap((result) =>
          from(
            sortedUniqBy(
              orderBy(result, [(value) => value.computeDate, (value) => value.beginPeriodDate], ['desc', 'asc'])
                .filter((value) => !value.originPayExecId && value.payrollType === PayrollType.NORMAL && value.payPeriodId !== undefined),
              (value) => value.beginPeriodDate
            )
          )
        ),
        mergeMap((payExec) => {
          return forkJoin([
            of(payExec),
            this.payValuesService
              .getPayValues(
                contractId,
                payExec.payPeriodId,
                payExec.payPeriodId,
                this.hawkTestCreationForm.get('rules').value
              )
              .pipe(
                map((payValues) => {
                  return payValues.map((payValue) => {
                    return <HawkAssertion>{
                      name: payValue.name,
                      value: payValue.allValue
                    };
                  });
                })
              )
          ]);
        }),
        map(([payExec, hawkAssertions]) => {
          return <HawkExecution>{
            beginDate: payExec.beginPeriodDate,
            endDate: payExec.endPeriodDate,
            executionDate: payExec.computeDate,
            assertions: hawkAssertions
          };
        })
      );
  }


  private populateInitialRules() {
    this.getPayRules(this.cliRegId)
      .pipe(
        tap((value) => {
          this.initialRules = [...value];
        }),
        catchError(() => {
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  get executions(): UntypedFormArray {
    return this.hawkTestCreationForm.controls['executions'] as UntypedFormArray;
  }

  execution(executionIndex: number): UntypedFormGroup {
    return this.executions.controls[executionIndex] as UntypedFormGroup;
  }

  assertions(executionIndex: number): UntypedFormGroup {
    return this.execution(executionIndex).controls['assertions'] as UntypedFormGroup;
  }

  public submit(): Observable<ModalCommand> {
    const formData = new FormData();
    formData.set('variables[GENERATE]', 'true');
    formData.set('variables[HAWK_CONTRACT_ID]', this.contractId.toString());
    formData.set('variables[HAWK_TEST_DATA]', JSON.stringify(this.hawkTestCreationForm.value));

    if (this.hawkTestUrl === undefined || this.hawkTestUrl === null || this.hawkTestUrl === '') {
      return of({state: ModalState.INVALID, message: 'unable to find hawk test url', duration: 1000});
    }

    return this.httpClient.post(this.hawkTestUrl, formData)
      .pipe(
        map(() => ({state: ModalState.VALID, message: 'ok', duration: 1000})),
        catchError(() => of({state: ModalState.INVALID, message: 'ko', duration: 1000}))
      );
  }

  public filterPayRulesBasedOnInput(input: string) {
    this.rules = [...this.initialRules];
    if (!isEmpty(input)) {
      this.rules = this.rules
        .filter((columnContent) => columnContent.toLowerCase().includes(input.toLowerCase()))
        .sort((a, b) =>
          a.toLowerCase().indexOf(input.toLowerCase()) < b.toLowerCase().indexOf(input.toLowerCase()) ? -1 : 1
        );
    }
    return this.rules;
  }

  public getPayRules(cliRegId: number): Observable<string[]> {
    const opts: ApiRequestOptions = REQUEST_OPTIONS.OBSERVE.RESPONSE;
    opts.requestOptions.params = {};
    const now: string = moment().startOf('day').format('YYYY-MM-DD');

    return this.apiService
      .get('/repository/api/v1/regulation/' + cliRegId + '/flatten/payRules?filter=@.endDate > \'' + now + '\'')
      .pipe(
        map((response: RegulationFlattensDTO) => {
          const payRules = response?.regulationPayFlatten?.payRules;

          return payRules.map((payRule) => payRule.name);
        })
      );
  }
}
